/**
 * Offline Context - Provides offline state and sync functionality to the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initDB } from '../services/offlineDB';
import {
  isOnline as checkOnline,
  syncAll,
  downloadForOffline,
  getPendingSyncCount,
  setupAutoSync
} from '../services/syncService';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children, user }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  // Initialize IndexedDB on mount
  useEffect(() => {
    initDB()
      .then(() => {
        console.log('✅ Offline database ready');
        setOfflineReady(true);
      })
      .catch((error) => {
        console.error('❌ Failed to initialize offline database:', error);
      });
  }, []);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingSyncCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error getting pending count:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('You are back online!', {
        description: pendingCount > 0 ? `${pendingCount} items waiting to sync` : undefined,
        action: pendingCount > 0 ? {
          label: 'Sync Now',
          onClick: () => manualSync()
        } : undefined
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline', {
        description: 'Changes will be saved locally and synced when online'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingCount]);

  // Setup auto-sync
  useEffect(() => {
    const cleanup = setupAutoSync((result) => {
      if (result.success > 0) {
        toast.success(`Synced ${result.success} items`);
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} items failed to sync`);
      }
      updatePendingCount();
      setLastSyncTime(new Date());
    });

    return cleanup;
  }, [updatePendingCount]);

  // Update pending count periodically
  useEffect(() => {
    updatePendingCount();
    const interval = setInterval(updatePendingCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [updatePendingCount]);

  // Manual sync function
  const manualSync = async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }

    if (isSyncing) {
      toast.info('Sync already in progress');
      return;
    }

    setIsSyncing(true);
    
    try {
      const result = await syncAll((progress) => {
        // Could update a progress indicator here
        console.log(`Sync progress: ${progress.current}/${progress.total}`);
      });

      if (result.total === 0) {
        toast.info('Everything is up to date');
      } else if (result.failed === 0) {
        toast.success(`Successfully synced ${result.success} items`);
      } else {
        toast.warning(`Synced ${result.success} items, ${result.failed} failed`);
      }

      setLastSyncTime(new Date());
      await updatePendingCount();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Sync failed: ' + error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  // Download data for offline use
  const downloadOfflineData = async () => {
    if (!isOnline) {
      toast.error('Cannot download while offline');
      return;
    }

    if (!user) {
      toast.error('Please log in first');
      return;
    }

    setIsDownloading(true);
    toast.info('Downloading data for offline use...', { duration: 2000 });

    try {
      await downloadForOffline(user.id, user.companyId, user.role);
      toast.success('Offline data ready!', {
        description: 'You can now use the app without internet'
      });
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download offline data');
    } finally {
      setIsDownloading(false);
    }
  };

  const value = {
    isOnline,
    isOffline: !isOnline,
    pendingCount,
    isSyncing,
    isDownloading,
    lastSyncTime,
    offlineReady,
    manualSync,
    downloadOfflineData,
    updatePendingCount
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
